/* ──────────────────────────────────────
   courtDurations — keeps the buyer from being offered a duration the court
   has no price for.

   `club.allowedDurations` says which durations the CLUB accepts; the pricing
   matrix of each COURT says which of those it can actually charge. They are
   not the same list. When a court prices 60 and 90 but not 120, the API still
   returns bookable starts for 120 (the resolver falls through to the club's
   base amount, usually 0), and the buyer used to see a "120 min — Consultar"
   option that nobody can honour.

   Pruning at the fetch boundary keeps every consumer — grid cells, the
   duration popover, the quick-slot modal — agreeing on what is bookable,
   instead of each one re-deciding what an unpriced slot means.
────────────────────────────────────── */

export function isPricedStart(entry) {
  const price = Number(entry?.price);
  return Number.isFinite(price) && price > 0;
}

/**
 * Drops unpriced bookable starts from an availability payload, and with them
 * any duration left with nothing to offer. Returns a new object; the input is
 * never mutated.
 */
export function pruneUnpricedStarts(availability) {
  const courts = Array.isArray(availability?.courts) ? availability.courts : [];

  return {
    ...availability,
    courts: courts.map((court) => {
      const starts = court?.bookableStarts;
      if (!starts || Array.isArray(starts)) return court;

      const pruned = {};
      Object.entries(starts).forEach(([duration, entries]) => {
        if (!Array.isArray(entries)) return;
        const priced = entries.filter(isPricedStart);
        if (priced.length > 0) pruned[duration] = priced;
      });

      return { ...court, bookableStarts: pruned };
    }),
  };
}

/**
 * Durations at least one court can actually price, in ascending order.
 * Falls back to `fallbackDurations` while availability is empty (first paint,
 * a failed fetch) so the duration tabs never render blank.
 */
export function durationsWithPricing(availability, fallbackDurations = []) {
  const courts = Array.isArray(availability?.courts) ? availability.courts : [];
  const found = new Set();

  courts.forEach((court) => {
    const starts = court?.bookableStarts;
    if (!starts || Array.isArray(starts)) return;
    Object.entries(starts).forEach(([duration, entries]) => {
      if (Array.isArray(entries) && entries.some(isPricedStart)) {
        found.add(Number(duration));
      }
    });
  });

  if (found.size === 0) return fallbackDurations;

  const allowed = new Set(fallbackDurations.map(Number));
  const priced = [...found].filter((d) => allowed.size === 0 || allowed.has(d));
  return priced.length > 0 ? priced.sort((a, b) => a - b) : fallbackDurations;
}
